import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { RiskListOnlyOptimizedResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetRiskListOnlyOptimizedQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC risk data to match the GraphQL query structure
 */
const mapTrpcRisksToGraphQL = (
  trpcData: RiskListOnlyOptimizedResponseRow[]
): GetRiskListOnlyOptimizedQuery => {
  return {
    risk: trpcData,
  };
};

export const useGetRiskListOnlyOptimized = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.risk.riskListOnlyOptimized.queryOptions(),
    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcRisksToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
